/* Built In Imports */
import NextLink from 'next/link';
import { useCallback, useContext, useEffect, useState } from 'react';

/* External Imports */
import { Box, Link, ListItem, UnorderedList } from '@chakra-ui/react';

/* Internal Imports */
/* Components */
import Breadcrum from '@components/Navigation/Breadcrum';
import HeadComponent from '@components/Utility/HeadComponent';
import Loader from '@components/Utility/Loader';
import config from '@config';
import MidContainer from '@Layout/MidContainer';
import WrapLayout from '@Layout/WrapLayout';
import { AuthContext } from '@store/auth-context';

/* Services */
import {
    consoleLog, getLocale
} from '@components/Utility/Shared/SharedService';
import {
    fetchFooterData, fetchHeaderMenuData, fetchHeaderTopMenuData
} from 'services/commonService';
import {
    fetchDetails,
    fetchReportsList,
    getLandingPaths
} from 'services/othersService';

/* Styles */

/**
 * @param {string} region - Current User Region
 * @param {string} language - Current User Language
 * @param {string} pageData - Page Data
 */
const IshaFoundationReports = ({ region, language, pageData }) => {
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [errMsg, setErrMsg] = useState('');
  const authContext = useContext(AuthContext);

  useEffect(() => {
    authContext.RegionAndLang(region, language);
  }, [authContext, region, language]);

  const fetchReports = useCallback(async () => {
    let response;
    try {
      response = await fetchReportsList();
    } catch (e) {
      consoleLog(e);
    }
    return response;
  }, []);

  useEffect(() => {
    fetchReports()
      .then(res => {
        // consoleLog('reports res ===>>>>> ', res);
        setLoading(false);
        if (res?.error_code != 403) {
          setReports(res);
        } else {
          setErrMsg(res?.message);
        }
      })
      .catch(err => {
        setLoading(false);
        // consoleLog('reports error ===>>>>> ', err.response);
        setReports([]);
      });
    return () => {
      setLoading(false);
    };
  }, [fetchReports]);

  return (
    <WrapLayout>
      <HeadComponent
        data={pageData?._seoMetaTags || pageData?.seoFields}
        availableLocals={pageData.availableLocales}
      >
        {' '}
      </HeadComponent>
      <Breadcrum
        page={pageData.breadcrumbs}
        alsoin={pageData.alsoin}
        url=""
        region={region}
        lang={language}
        pageConfig={pageData?.pageConfig}
      />
      <Box as="section" className="page-wrap container-article" p="0">
        {loading && (
          <Box margin="20px">
            <Loader key="upcoming-null" pos="relative" />
          </Box>
        )}
        <MidContainer w={768}>
          <Box className="event-text" m="100px 0 150px 0">
            <UnorderedList fontFamily="FedraSansStd-Medium" fontSize="18px">
              {!loading &&
                reports?.map((report, i) => {
                  return (
                    <ListItem display="inline-block" mb="25px" key={i}>
                      <NextLink
                        href={`/${region}/${language}/isha-foundation/reports/view?id=${report.id}`}
                        passHref
                        legacyBehavior>
                        <Link
                          fontFamily={'FedraSansStd-book'}
                          _hover={{ borderBottom: '1px solid #cf4520' }}
                          w="auto"
                          height={{ base: '105px' }}
                        >
                          {report.label}
                        </Link>
                      </NextLink>
                    </ListItem>
                  );
                })}
            </UnorderedList>
          </Box>
        </MidContainer>
        {!loading && errMsg && (
          <Box margin="50px" textAlign="center">
            <b>{errMsg}</b>
          </Box>
        )}
      </Box>
    </WrapLayout>
  );
};

export const getStaticPaths = async () => {
  const outreachLandingPaths = config.USE_LOCAL_URL
    ? [`/in/en/isha-foundation/reports`]
    : await getLandingPaths('L1', '/isha-foundation/reports');
  return {
    paths: outreachLandingPaths,
    fallback: false
  };
};

export const getStaticProps = async ({
  preview,
  previewData,
  params: { region, language },
}) => {
  const headerTopMenuData = await fetchHeaderTopMenuData(
    getLocale(region, language),
    'isotopmenu'
  );
  const headerMenuData = await fetchHeaderMenuData(
    getLocale(region, language),
    'isomainmenu'
  );
  const footerData = await fetchFooterData(
    getLocale(region, language),
    'isofooter'
  );
  const pageData = await fetchDetails(
    region,
    language,
    `isha-foundation/reports`,
    { preview, data: previewData }
  );

  return {
    props: {
      headerTopMenuData: headerTopMenuData?.data || null,
      headerMenuData: headerMenuData?.data || null,
      region: region,
      language: language,
      footerData: footerData?.data || null,
      pageData: pageData || null,
      isPreview: preview ? true : false
    }
  };
};

export default IshaFoundationReports;
